import React, { Component } from 'react';
import i18next from 'i18next';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isArray } from 'util';
import get from 'lodash.get';

import AIMS_FLAVORS from 'lib/aimsFlavors';
import { reduceCSL } from 'lib/arrayUtils';
import {
  content as ContentPropType,
  teaseStyles as TeaseStylesPropType,
} from 'lib/CustomPropTypes';
import { imageAltForImage } from 'lib/imageAlt';
import { hasFeaturedAuthor } from 'lib/featuredAuthor';
import { teaseCard as paidContent } from 'lib/PaidContent';
import {
  getVerticalName,
  getVerticalModuleClassName,
} from 'lib/verticalType';

import buildUrl from 'lib/buildUrl';

import Ad from 'components/Ad';
import Dek from 'components/Dek';
import Link from 'components/Link';
import PaidContent from 'components/PaidContent';
import Picture from 'components/Picture';
import RelatedContent from 'components/RelatedContent';
import TeaseCategory from 'components/TeaseCategory';
import ContentTimestamp from 'components/ContentTimestamp';
import styles from './styles.module.scss';

const block = 'teaseCard';

class TeaseCard extends Component {
  static propTypes = {
    children: PropTypes.node,
    content: ContentPropType.isRequired,
    imageClick: PropTypes.func,
    isRailAdjacent: PropTypes.bool,
    isMobileOnlyComponent: PropTypes.bool,
    packageId: PropTypes.string,
    pkgClassName: PropTypes.string,
    showFeaturedAuthor: PropTypes.bool,
    showAuthor: PropTypes.bool,
    showDek: PropTypes.bool,
    showTeaseImage: PropTypes.bool,
    showTimestamp: PropTypes.bool,
    showLive: PropTypes.bool,
    size: PropTypes.string,
    styles: TeaseStylesPropType,
    vertical: PropTypes.string,
  };

  static contextTypes = {
    section: PropTypes.string,
  };

  static defaultProps = {
    children: null,
    showAuthor: false,
    showFeaturedAuthor: false,
    showTimestamp: false,
    showDek: false,
    showTeaseImage: true,
    imageClick: null,
    isRailAdjacent: false,
    isMobileOnlyComponent: false,
    showLive: false,
    packageId: null,
    pkgClassName: null,
    size: 'small',
    styles: {
      content: '',
      picture: '',
      pictureLink: '',
      info: '',
      title: '',
      featuredAuthor: '',
      dek: '',
      byline: '',
      author: '',
      date: '',
      featuredAuthorImage: '',
      live: '',
    },
    vertical: '',
  };

  getClassName() {
    const featuredAuthor = this.hasFeaturedAuthor();
    const nativeAd = this.isNative();

    const { styles: { content: contentStyles } } = this.props;

    const classes = classNames(
      block,
      styles.content,
      contentStyles,
      {
        [styles.sponsored]: nativeAd,
        noMainImage: featuredAuthor || !this.hasImage(),
        hasFeaturedAuthor: featuredAuthor,
      },
    );

    return classes;
  }

  getCategoryStyle() {
    if (this.isNative()) {
      return 'sponsored';
    }
    if (this.hasFeaturedAuthor()) {
      return 'hasFeaturedAuthor';
    }
    const { styles: { eyebrow: eyebrowStyles } } = this.props;
    return eyebrowStyles || '';
  }

  getPaidSponsor() {
    const { content } = this.props;
    const paidContentName = content
      && content.item
      && content.item.source
      && content.item.source.name;

    const organization = content
      && content.item
      && content.item.source
      && content.item.source.organization;
    const { name, externalUrl } = organization || {};

    if (paidContentName) {
      return paidContentName;
    }
    if (!name) {
      return null;
    }

    const sponsorToolkit = ['pb5', 'mt0', 'mb0'];
    const sponsorTitleToolkit = ['gray-60', 'f4', 'lh-none', 'founders-cond', 'ls-normal', 'tl'];
    const titleSpan = (
      <span className={classNames(sponsorTitleToolkit)}>
        {' '}
        {name}
        {' '}
      </span>
    );
    if (externalUrl) {
      return (
        <p className={classNames(styles.paidSponsor, sponsorToolkit)}>
          <Link to={externalUrl}>
            {titleSpan}
          </Link>
        </p>
      );
    }
    return (
      <p className={classNames(styles.paidSponsor, sponsorToolkit)}>
        {titleSpan}
      </p>
    );
  }

  hasFeaturedAuthor() {
    const {
      showFeaturedAuthor,
      content,
      content: {
        computedValues,
        type,
      },
    } = this.props;
    const { item } = computedValues && computedValues.item ? computedValues : content;

    if (!showFeaturedAuthor) return false;

    return hasFeaturedAuthor(item, type);
  }

  hasImage() {
    const {
      content,
      content: {
        computedValues,
      },
      showTeaseImage,
    } = this.props;

    const {
      coverImage,
      mainImage,
      teaseImage,
    } = computedValues || content;

    return showTeaseImage === true && (coverImage || mainImage || teaseImage);
  }

  isNative() {
    const { content, vertical } = this.props;
    const { type } = content;
    const { nativeAd } = content.item || content;

    return (type === 'article' || type === 'video') && nativeAd && vertical !== 'sponsoredcontent';
  }

  author() {
    const {
      content: {
        item,
      },
      showAuthor,
      styles: {
        author: authorStyles,
      },
    } = this.props;

    if (!item || !item.authors) {
      return null;
    }

    // Filter to the first four 'authors'
    const authors = item.authors
      .filter((author, i) => author.authorType === 'author' && i < 4);

    if (!this.hasFeaturedAuthor() && showAuthor && isArray(authors) && authors.length) {
      return (
        <span className={classNames(styles.author, authorStyles, 'black')}>
          {authors.map((author) => author.person.name).reduce(reduceCSL)}
        </span>
      );
    }
    return null;
  }

  picture(articleModuleClassName) {
    if (this.hasFeaturedAuthor()) {
      return null;
    }

    const {
      children,
      content,
      imageClick,
      pkgClassName,
      size,
      styles: propStyles = {},
      vertical,
    } = this.props;

    const {
      type,
      computedValues,
    } = content;

    const url = buildUrl(content);

    // computedValues is not always there, depending on who calls.  In video fronts, unibrow
    // is a first class citizen, and computedValues does not exist on content
    const unibrow = computedValues ? computedValues.unibrow : content.unibrow;

    // Add missing child element properties
    const childElements = React.Children.map(children, (child) => React.cloneElement(child, {
      type,
      ...child.props,
    }));

    const isStraightUp = pkgClassName === 'pkg straightUp';

    return (
      /* eslint-disable jsx-a11y/no-static-element-interactions */
      /* eslint-disable jsx-a11y/click-events-have-key-events */
      <div
        className={classNames(
          `${block}__picture`,
          articleModuleClassName,
          propStyles.picture,
          {
            'self-start': isStraightUp,
          },
        )}
        onClick={imageClick}
      >
        <Link
          to={url}
          className={classNames(styles.pictureLink, propStyles.pictureLink)}
        >
          {childElements}
        </Link>
        <TeaseCategory
          type={type}
          unibrow={unibrow}
          style={this.getCategoryStyle()}
          size={size}
          iconUrl={url}
          vertical={vertical}
        />
      </div>
      /* eslint-enable jsx-a11y/click-events-have-key-events */
      /* eslint-enable jsx-a11y/no-static-element-interactions */
    );
  }

  date() {
    const {
      content,
      showTimestamp,
      styles: {
        date: dateStyles,
      },
    } = this.props;

    if (!get(content, 'item') || !showTimestamp) {
      return null;
    }

    const dateCreated = get(content, 'item.dateCreated');
    const datePublished = get(content, 'item.datePublished');

    return (
      <span className={classNames(styles.date, dateStyles)}>
        <ContentTimestamp
          showCreatedDate
          delimiter=" / "
          dateCreated={dateCreated}
          datePublished={datePublished}
        />
      </span>
    );
  }

  dek() {
    const {
      content,
      showDek,
      styles: {
        dek: dekStyles,
      },
    } = this.props;
    const { dek } = content.computedValues || content;

    if (!showDek || !dek) {
      return null;
    }

    return (
      <Dek
        dek={dek}
        styles={dekStyles}
      />
    );
  }

  featuredAuthor(articleModuleClassName) {
    const name = get(this.props, 'content.item.authors[0].person.name', null);
    const {
      styles: {
        featuredAuthor: featuredAuthorStyles,
      },
    } = this.props;
    const classes = classNames(styles.featuredAuthor, featuredAuthorStyles, 'card__featuredAuthorName');

    return (
      <span className={classNames(`${block}__featuredAuthor`, articleModuleClassName)}>
        <span className={classes}>
          {name}
          <span>&#32;</span>
        </span>
      </span>
    );
  }

  featuredAuthorImage(articleModuleClassName) {
    const divClasses = classNames(
      `${block}__featuredAuthorImage`,
      styles.featuredAuthorImageContainer,
      articleModuleClassName,
    );
    const {
      content,
      content: {
        computedValues,
        type,
      },
      size,
      styles: {
        featuredAuthorImage: featuredAuthorImageStyles,
      },
    } = this.props;
    const classes = classNames(styles.featuredAuthorImage, featuredAuthorImageStyles);
    const author = get(this.props, 'content.item.authors[0].person', null);
    const url = buildUrl(content);

    if (!author.primaryImage) {
      return null;
    }

    return (
      <div className={divClasses}>
        <TeaseCategory
          type={type}
          // computedValues is not always there, depending on who calls.  In video fronts, unibrow
          // is a first class citizen, and computedValues does not exist on this.props.content
          unibrow={(computedValues && computedValues.unibrow) || content.unibrow}
          style={this.getCategoryStyle()}
          size={size}
          iconUrl={url}
        />
        <div className={classes}>
          <Link to={url}>
            <Picture
              responsiveFlavors={{ s: AIMS_FLAVORS.FOCAL_320x320 }}
              url={author.primaryImage.url.primary}
              alt={imageAltForImage(author.primaryImage)}
            />
          </Link>
        </div>
      </div>
    );
  }

  relatedContent() {
    const {
      content: { related = [] },
      showTeaseImage,
      isRailAdjacent,
      pkgClassName,
    } = this.props;

    return (
      <RelatedContent
        isRailAdjacent={isRailAdjacent}
        parentNoImage={showTeaseImage === false}
        pkgClassName={pkgClassName}
        related={related}
      />
    );
  }

  headline(featuredAuthor, articleModuleClassName) {
    const {
      content,
      styles: {
        title: titleStyles,
      },
      vertical,
    } = this.props;
    const isPaidHub = vertical === 'sponsoredcontent';
    const publicoHeadlines = ['knowyourvalue', 'think', 'today'];
    const isPaidContent = content.item && content.item.nativeAd;

    const url = buildUrl(content);
    let headline;
    if (typeof content.computedValues !== 'undefined') {
      headline = content.computedValues && content.computedValues.headline;
    } else {
      headline = content.headline.actual || content.headline.primary;
    }


    return (
      <h2
        className={classNames(
          `${block}__headline`,
          styles.title,
          titleStyles,
          {
            relative: content.related,
            [styles.sponsored]: isPaidContent,
          },
        )}
      >
        <Link to={url}>
          {featuredAuthor && this.featuredAuthor(articleModuleClassName)}
          <span
            className={classNames(
              styles.headline,
              !this.hasImage() && (publicoHeadlines.includes(vertical) ? 'lh-title' : 'lh-none'),
              {
                [styles.paidContent]: isPaidContent && !isPaidHub,
              },
            )}
          >
            {headline}
          </span>
        </Link>

        <span
          className={classNames(
            {
              [styles.paidContentSponsor]: isPaidContent,
              'founders-mono': isPaidContent,
            },
          )}
        >

          {this.isNative() && !isPaidHub && this.getPaidSponsor()}
        </span>
      </h2>
    );
  }

  live() {
    const {
      showLive,
      styles: {
        liveText: liveTextStyles,
      },
    } = this.props;

    if (!showLive) {
      return null;
    }

    return (
      <div className={styles.live}>
        <div className={styles.liveIcon}>
          <svg height="21" width="21">
            <circle cx="10" cy="10" r="10px" stroke="red" strokeWidth="1" fill="none" />
            <circle cx="10" cy="10" r="5px" fill="red" />
          </svg>
        </div>
        <div className={classNames(styles.liveText, liveTextStyles)}>
          { i18next.t('LIVE NOW') }
        </div>
      </div>
    );
  }

  nativeAd() {
    const { content } = this.props;

    if (content.nativeAd) {
      return <Ad slot="native" />;
    }

    return null;
  }

  nativeTrackingPixel() {
    const { content } = this.props;

    if (content.thirdPartyTrackingUrl) {
      return <img className="native-tracking-pixel" src={content.thirdPartyTrackingUrl} alt="" />;
    }

    return null;
  }

  render() {
    const {
      content,
      isMobileOnlyComponent,
      showTeaseImage,
      styles: propStyles,
      packageId,
      pkgClassName,
      size,
      vertical,
    } = this.props;

    const { type, computedValues, id } = content;
    const isPaidHub = vertical === 'sponsoredcontent';
    const isPaidContent = content.item && content.item.nativeAd;
    const isStraightUp = pkgClassName === 'pkg straightUp';

    if (type === 'ad') {
      return <Ad slot="boxinline" offsetViewport={50} />;
    }

    if (type === paidContent.type && !isPaidHub) {
      return (
        <PaidContent
          isMobileOnlyComponent={isMobileOnlyComponent}
          {...content}
          className={classNames(
            styles.content,
            propStyles.content,
          )}
        />
      );
    }

    const url = buildUrl(content);

    const hasImage = this.hasImage();
    const featuredAuthor = this.hasFeaturedAuthor();

    // computedValues is not always there, depending on who calls.  In video fronts, unibrow
    // is a first class citizen, and computedValues does not exist on this.props.content
    const unibrow = computedValues ? computedValues.unibrow : content.unibrow;
    const verticalName = getVerticalName(content);
    const articleModuleClassName = classNames({
      [getVerticalModuleClassName(verticalName)]: verticalName === 'think',
    });

    const bylineToolkitClasses = ['founders-mono', 'f2', 'lh-copy', 'ls-tight', 'gray-80', 'ma0', 'mt3'];


    return (
      <article data-packageid={packageId} data-contentid={id} className={this.getClassName()}>
        {this.nativeAd()}
        {this.nativeTrackingPixel()}
        {hasImage && !featuredAuthor && this.picture(articleModuleClassName)}

        <div
          className={classNames(
            styles.info,
            'pt3 pt0 ph4',
            propStyles.info,
            articleModuleClassName,
            {
              'bg-knockout-primary': isPaidHub,
              'flex-wrap': isStraightUp,
              [styles.paidContent]: isPaidContent,
            },
          )}
          data-test="tease-card__info"
        >
          {this.live()}
          {!featuredAuthor && !hasImage && (
            <TeaseCategory
              type={type}
              unibrow={unibrow}
              style={this.getCategoryStyle()}
              size={size}
              iconUrl={url}
              defaultSpacing={false}
              vertical={vertical}
            />
          )}
          {featuredAuthor && (
            <span className={articleModuleClassName}>
              <span className={classNames('icon', 'icon-quote-mark', styles.quote)} />
            </span>
          )}
          {this.headline(featuredAuthor, articleModuleClassName)}
          {this.dek()}
          {featuredAuthor && !this.featuredAuthorImage(articleModuleClassName) && (
            <p
              className={classNames(
                `${block}__articleFeaturedAuthor`,
                styles.byline,
                propStyles.byline,
                styles.opinion,
                bylineToolkitClasses,
              )}
            >
              <span
                className={classNames(
                  articleModuleClassName,
                  'pr',
                  propStyles.eyebrow,
                )}
              >
                {unibrow && unibrow.text && unibrow.text.toUpperCase()}
              </span>
            </p>
          )}
          {(this.author() || this.date()) && (
            <p
              className={classNames(
                `${block}__byline`,
                styles.byline,
                propStyles.byline,
                bylineToolkitClasses,
              )}
            >
              {this.author()}
              {this.date()}
            </p>
          )}

          {featuredAuthor && this.featuredAuthorImage(articleModuleClassName)}

          {showTeaseImage && this.relatedContent()}
        </div>
        {showTeaseImage === false && this.relatedContent()}
      </article>
    );
  }
}

export default TeaseCard;
